import type { Schedule } from '../../services/adminApi';
import type { TeamOverrideActiveSummaryItem, TeamOverrideRequest, TeamOverrideTargetType } from '../../services/tenantApi';
import { parseDateTimeLocalInTimezone } from './scheduleTimezone';

/** Mirrors the BE-086 server rules so the wizard can fail fast; the server remains authoritative. */
export const OVERRIDE_NAME_MAX = 150;
export const OVERRIDE_REASON_MAX = 500;
export const OVERRIDE_BACKDATE_TOLERANCE_MS = 5 * 60 * 1000;
export const OVERRIDE_PAST_DAYS = 90;

export type OverrideWizardStep = 'schedules' | 'target' | 'timing' | 'details' | 'review';
export const OVERRIDE_WIZARD_STEPS: OverrideWizardStep[] = ['schedules', 'target', 'timing', 'details', 'review'];

export type ScheduleTypeFilter = 'all' | 'Primary' | 'Escalation';

export interface OverrideDraft {
  scheduleIds: string[];
  targetType: TeamOverrideTargetType;
  targetId: string;
  mode: 'now' | 'window';
  windowStartLocal: string;
  windowEndLocal: string;
  name: string;
  reason: string;
  confirmLargeGroup: boolean;
}

export const emptyOverrideDraft = (): OverrideDraft => ({
  scheduleIds: [],
  targetType: 'User',
  targetId: '',
  mode: 'now',
  windowStartLocal: '',
  windowEndLocal: '',
  name: '',
  reason: '',
  confirmLargeGroup: false,
});

export function scheduleTypeOf(schedule: Pick<Schedule, 'scheduleType'>): 'Primary' | 'Escalation' {
  return String(schedule.scheduleType || '').toLowerCase() === 'escalation' ? 'Escalation' : 'Primary';
}

/** Active schedules matching the type filter and every whitespace-separated term of the name filter (case-insensitive). */
export function filterOverrideSchedules<T extends Pick<Schedule, 'name' | 'scheduleType' | 'isActive'>>(
  schedules: T[], text: string, type: ScheduleTypeFilter,
): T[] {
  const terms = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return schedules
    .filter(s => s.isActive !== false)
    .filter(s => type === 'all' || scheduleTypeOf(s) === type)
    .filter(s => terms.every(term => String(s.name || '').toLowerCase().includes(term)))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
}

/** Toggles every filtered schedule on (or off when they are all already selected), keeping other selections. */
export function toggleAllFiltered(selected: string[], filteredIds: string[]): string[] {
  const set = new Set(selected);
  const allSelected = filteredIds.length > 0 && filteredIds.every(id => set.has(id));
  if (allSelected) filteredIds.forEach(id => set.delete(id));
  else filteredIds.forEach(id => set.add(id));
  return Array.from(set);
}

export interface OverrideTargetInfo {
  /** Active members when the target is a group (null while loading or for a user). */
  activeMemberCount: number | null;
  maxGroupSize: number;
}

/** Returns the first blocking problem for a wizard step, or null when the step is complete. */
export function validateOverrideStep(step: OverrideWizardStep, draft: OverrideDraft, timezone: string, nowMs: number, target?: OverrideTargetInfo): string | null {
  switch (step) {
    case 'schedules':
      return draft.scheduleIds.length === 0 ? 'Select at least one schedule.' : null;
    case 'target':
      if (!draft.targetId) return draft.targetType === 'Group' ? 'Select a group.' : 'Select a user.';
      if (draft.targetType === 'Group' && target?.activeMemberCount === 0) return 'The selected group has no active members.';
      if (draft.targetType === 'Group' && target?.activeMemberCount != null && target.activeMemberCount > target.maxGroupSize && !draft.confirmLargeGroup)
        return `This group has ${target.activeMemberCount} active members; confirm that all of them should be paged.`;
      return null;
    case 'timing': {
      if (draft.mode === 'now') return null;
      if (!draft.windowStartLocal || !draft.windowEndLocal) return 'Enter both a start and an end time.';
      const start = parseDateTimeLocalInTimezone(draft.windowStartLocal, timezone).getTime();
      const end = parseDateTimeLocalInTimezone(draft.windowEndLocal, timezone).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end)) return 'Enter valid start and end times.';
      if (end <= start) return 'The end must be after the start.';
      if (start < nowMs - OVERRIDE_BACKDATE_TOLERANCE_MS) return 'The start cannot be in the past.';
      return null;
    }
    case 'details':
      if (!draft.name.trim()) return 'Enter a name for the override.';
      if (draft.name.trim().length > OVERRIDE_NAME_MAX) return `The name must be ${OVERRIDE_NAME_MAX} characters or fewer.`;
      if (draft.reason.trim().length > OVERRIDE_REASON_MAX) return `The reason must be ${OVERRIDE_REASON_MAX} characters or fewer.`;
      return null;
    case 'review':
      for (const s of OVERRIDE_WIZARD_STEPS.slice(0, -1)) {
        const problem = validateOverrideStep(s, draft, timezone, nowMs, target);
        if (problem) return problem;
      }
      return null;
    default:
      return null;
  }
}

export function buildOverrideRequest(draft: OverrideDraft, timezone: string): TeamOverrideRequest {
  const request: TeamOverrideRequest = {
    name: draft.name.trim(),
    reason: draft.reason.trim() || undefined,
    targetType: draft.targetType,
    targetId: draft.targetId,
    scheduleIds: draft.scheduleIds,
    mode: draft.mode,
  };
  if (draft.mode === 'window') {
    request.startUtc = parseDateTimeLocalInTimezone(draft.windowStartLocal, timezone).toISOString();
    request.endUtc = parseDateTimeLocalInTimezone(draft.windowEndLocal, timezone).toISOString();
  }
  if (draft.confirmLargeGroup) request.confirmLargeGroup = true;
  return request;
}

export function overrideTargetLabel(item: { targetType: string; targetName?: string | null; targetUserCount?: number }): string {
  const name = item.targetName || (String(item.targetType).toLowerCase() === 'group' ? 'Unknown group' : 'Unknown user');
  return String(item.targetType).toLowerCase() === 'group' ? `${name} (group, ${item.targetUserCount ?? 0} members)` : name;
}

/** Banner text: "<name> → <target> (<n> schedules) – since <start>, until <end | ended manually>". */
export function describeActiveOverride(item: TeamOverrideActiveSummaryItem, formatWhen: (iso: string) => string): string {
  const count = item.scheduleIds?.length ?? item.scheduleNames?.length ?? 0;
  const until = item.openEnded || !item.endUtc ? 'until ended manually' : `until ${formatWhen(item.endUtc)}`;
  return `${item.name} → ${overrideTargetLabel(item)} (${count} ${count === 1 ? 'schedule' : 'schedules'}) – since ${formatWhen(item.startUtc)}, ${until}`;
}

const ERROR_MESSAGES: Record<string, string> = {
  schedules_required: 'Select at least one schedule.',
  schedule_invalid: 'One or more selected schedules are inactive or no longer exist.',
  target_not_found: 'The selected target no longer exists.',
  target_inactive: 'The selected user is not active.',
  target_group_empty: 'The selected group has no active members.',
  large_group_confirmation_required: 'This group is large; confirm that all members should be paged.',
  window_in_past: 'The start cannot be in the past.',
  window_invalid: 'The end must be after the start.',
  override_overlap: 'Another override already covers some of these schedules at that time.',
  override_not_active: 'The override is no longer active.',
  override_not_scheduled: 'The override has already started or was cancelled.',
  override_started: 'The override has already started; end it instead.',
  forbidden: 'Only tenant administrators can manage on-call overrides.',
};

export function overrideErrorMessage(err: unknown, fallback: string): string {
  const code = (err as { code?: string })?.code;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  return (err as Error)?.message || fallback;
}
